import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const COL_STEP = 268; // w-64 column + gap-3

// Horizontal scroll wrapper: hides the board's own x-scrollbar on md+ and shows a synced sticky bar + ‹ › buttons.
export function KanbanHScroll({ children, testid }) {
  const boardRef = useRef(null);
  const barRef = useRef(null);
  const [m, setM] = useState({ sw: 0, cw: 0, left: 0, tw: 0 });

  const measure = useCallback(() => {
    const b = boardRef.current; if (!b) return;
    setM({ sw: b.scrollWidth, cw: b.clientWidth, left: b.scrollLeft, tw: barRef.current?.clientWidth || 0 });
  }, []);

  useEffect(() => {
    const b = boardRef.current; if (!b) return;
    const ro = new ResizeObserver(measure);
    ro.observe(b); if (b.firstElementChild) ro.observe(b.firstElementChild); if (barRef.current) ro.observe(barRef.current);
    const onWheel = (e) => {
      if (!e.shiftKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault(); b.scrollLeft += e.deltaY;
    };
    b.addEventListener("wheel", onWheel, { passive: false });
    return () => { ro.disconnect(); b.removeEventListener("wheel", onWheel); };
  }, [measure]);

  const dragRef = useRef(null);
  const maxL = Math.max(0, m.sw - m.cw);
  const thumbW = m.sw ? Math.max(40, (m.tw * m.cw) / m.sw) : 0;
  const thumbX = maxL ? (m.left / maxL) * (m.tw - thumbW) : 0;
  const ratio = m.tw - thumbW > 0 ? maxL / (m.tw - thumbW) : 0;

  const onThumbDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, left: boardRef.current.scrollLeft };
  };
  const onThumbMove = (e) => {
    if (!dragRef.current) return;
    boardRef.current.scrollLeft = dragRef.current.left + (e.clientX - dragRef.current.x) * ratio;
  };
  const onThumbUp = () => { dragRef.current = null; };
  const onTrackDown = (e) => {
    const r = barRef.current.getBoundingClientRect();
    boardRef.current.scrollTo({ left: (e.clientX - r.left - thumbW / 2) * ratio, behavior: "smooth" });
  };
  const step = (dir) => {
    const b = boardRef.current;
    const n = Math.max(1, Math.floor(b.clientWidth / COL_STEP) - 1);
    b.scrollBy({ left: dir * n * COL_STEP, behavior: "smooth" });
  };

  const overflow = m.sw > m.cw + 1;
  const canL = m.left > 0;
  const canR = m.left + m.cw < m.sw - 1;
  const btn = "h-7 w-7 inline-flex items-center justify-center rounded-sm border border-slate-700 bg-slate-900 text-slate-200 hover:bg-blue-600 hover:border-blue-500 disabled:opacity-30 disabled:hover:bg-slate-900 transition-colors shrink-0";

  return (
    <>
      <div ref={boardRef} onScroll={measure} className="overflow-x-auto kanban-scroll kanban-hide-x pb-4" data-testid={testid}>
        {children}
      </div>
      <div className={`${overflow ? "hidden md:flex" : "hidden"} sticky bottom-0 lg:bottom-8 z-20 items-center gap-2 pl-1 pr-20 py-1.5 bg-slate-950/90 backdrop-blur border-t border-slate-800`} data-testid="kanban-hscroll-bar">
        <button type="button" className={btn} disabled={!canL} onClick={() => step(-1)} aria-label="Önceki kolonlar" data-testid="kanban-scroll-left">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div ref={barRef} onPointerDown={onTrackDown} className="kanban-hbar relative flex-1 h-3 rounded-full bg-slate-800 cursor-pointer" data-testid="kanban-hscroll-track">
          <div onPointerDown={onThumbDown} onPointerMove={onThumbMove} onPointerUp={onThumbUp} onPointerCancel={onThumbUp}
            className="absolute top-0 h-3 rounded-full bg-slate-500 hover:bg-blue-500 active:bg-blue-500 cursor-grab active:cursor-grabbing touch-none transition-colors"
            style={{ width: thumbW, transform: `translateX(${thumbX}px)` }} data-testid="kanban-hscroll-thumb" />
        </div>
        <button type="button" className={btn} disabled={!canR} onClick={() => step(1)} aria-label="Sonraki kolonlar" data-testid="kanban-scroll-right">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}
